import { Typography } from '@/components/ui/typography'
import { formatCurrency } from '@/lib/utils'
import type {
  SalesOrderPaymentBalance,
  SalesOrderPaymentLine,
  SalesOrderPaymentTone,
} from './sales-order-payments'

export const PAYMENT_TONES = {
  success: { surface: 'successSubtle', rail: 'success', text: 'success' },
  warning: { surface: 'warning', rail: 'warning', text: 'warning' },
  destructive: { surface: 'destructiveSubtle', rail: 'destructive', text: 'destructive' },
  neutral: { surface: 'subtle', rail: 'none', text: 'default' },
} as const satisfies Record<SalesOrderPaymentTone, { surface: string; rail: string; text: string }>

type SalesOrderPaymentLinesProps = {
  payment: { lines: SalesOrderPaymentLine[]; balance: SalesOrderPaymentBalance }
}

// Receipt lines under Total: each payment taken off, each refund sent back, then what the
// order still owes. An order change is a note without an amount, as Total already includes it.
export function SalesOrderPaymentLines({ payment }: SalesOrderPaymentLinesProps) {
  const balanceTone = PAYMENT_TONES[payment.balance.tone]

  return (
    <div data-testid="sales-order-payments" className="space-y-3 text-sm">
      {payment.lines.length > 0 ? (
        <ul className="space-y-3 border-t border-border/60 pt-3">
          {payment.lines.map((line) => (
            <li key={line.id} className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-5">
              <Typography
                variant="bodySmall"
                className={line.amount === null ? 'col-span-2 min-w-0' : 'min-w-0'}
              >
                {line.label}
              </Typography>
              {line.amount === null ? null : (
                <Typography
                  variant="bodySmall"
                  tone={line.kind === 'refund' ? 'destructive' : 'default'}
                  align="right"
                  className="tabular-nums"
                >
                  {formatCurrency(line.amount)}
                </Typography>
              )}
              {line.detail ? (
                <Typography variant="caption" className="col-span-2 mt-0.5 block">
                  {line.detail}
                </Typography>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-5 border-t border-border/60 pt-3">
        <Typography variant="label" weight="semibold" tone={balanceTone.text}>
          {payment.balance.label}
        </Typography>
        <Typography
          variant="label"
          weight="semibold"
          tone={balanceTone.text}
          align="right"
          className="tabular-nums"
        >
          {formatCurrency(payment.balance.amount)}
        </Typography>
      </div>
    </div>
  )
}
